import { useEffect, useState, type RefObject } from 'react'

export interface DiffSelection {
  path: string
  side: 'left' | 'right'
  startLine: number
  endLine: number
  rect: DOMRect
}

function findDiffLine(node: Node | null): HTMLElement | null {
  if (!node) return null
  const el = node.nodeType === Node.ELEMENT_NODE ? (node as HTMLElement) : node.parentElement
  if (!el) return null
  return el.closest('[data-line][data-path][data-side]') as HTMLElement | null
}

function deriveSelection(container: HTMLElement): DiffSelection | null {
  const sel = window.getSelection()
  if (!sel || sel.isCollapsed || sel.rangeCount === 0) return null

  const range = sel.getRangeAt(0)

  if (!container.contains(range.startContainer) || !container.contains(range.endContainer)) {
    return null
  }

  const startEl = findDiffLine(range.startContainer)
  const endEl = findDiffLine(range.endContainer)
  if (!startEl || !endEl) return null

  const startPath = startEl.dataset.path
  const endPath = endEl.dataset.path
  if (!startPath || startPath !== endPath) return null

  const startSide = startEl.dataset.side as 'left' | 'right' | undefined
  const endSide = endEl.dataset.side as 'left' | 'right' | undefined
  if (!startSide || startSide !== endSide) return null

  const startLineNum = parseInt(startEl.dataset.line ?? '', 10)
  const endLineNum = parseInt(endEl.dataset.line ?? '', 10)
  if (!Number.isFinite(startLineNum) || !Number.isFinite(endLineNum)) return null

  const rect = range.getBoundingClientRect()
  if (rect.width === 0 && rect.height === 0) return null

  return {
    path: startPath,
    side: startSide,
    startLine: Math.min(startLineNum, endLineNum),
    endLine: Math.max(startLineNum, endLineNum),
    rect,
  }
}

export function useDiffSelection(containerRef: RefObject<HTMLElement | null>): DiffSelection | null {
  const [selection, setSelection] = useState<DiffSelection | null>(null)

  useEffect(() => {
    let frame = 0
    const handler = () => {
      if (frame !== 0) return
      frame = requestAnimationFrame(() => {
        frame = 0
        const container = containerRef.current
        if (!container) {
          setSelection(null)
          return
        }
        setSelection(deriveSelection(container))
      })
    }
    document.addEventListener('selectionchange', handler)
    return () => {
      document.removeEventListener('selectionchange', handler)
      if (frame !== 0) cancelAnimationFrame(frame)
    }
  }, [containerRef])

  return selection
}
